import { IGcpAppDispatch } from "../redux/store";
import { getRouterState, IStoreState } from "../redux/rootReducer";
import { ensureUserLoggedIn } from "../redux/modules/auth/asyncActions";
import { IGcpAppIo } from "../redux/io";
import config from "../config";
import {
  extractUriRoute,
  generateRedirectURL,
  getRedirectForViewMode,
  putRoute,
  restoreLocation,
} from "../redux/modules/router/routes";
import { getViewMode } from "../redux/modules/router/reducer";
import {
  ACTIVE_FM_COLLAPSED,
  ACTIVE_FM_EXPANDED,
  APP_IS_OPENED,
  REQUESTED_FM_COLLAPSED,
  REQUESTED_FM_EXPANDED,
  SIDEBAR_OPENED,
  TO_ASANA_SELECTED,
  TO_BACKOFFICE_SELECTED,
} from "../tracking/constants";
import { toggleSidebarVisibility } from "../redux/modules/ui/reducer";
import { getPayloadFromToken } from "../redux/modules/auth/utils";
import { sentrySingleton } from "../libs/sentry/init";
import { intercomSingleton } from "../libs/intercom/init";
import { fetchUserIdentity } from "../apiFacades/intercom/userIdentityGenerator";

// resolves after we receive first route
// TODO: find a way how declare generators
const startRouteLoop = (
  dispatch: IGcpAppDispatch,
  listenHashChangeEvent: any,
) =>
  new Promise<void>((resolve) => {
    const routeLoopIteration = async (routeGen: IterableIterator<any>) => {
      const yielded = await routeGen.next();
      const newRoute = yielded.value;
      dispatch(putRoute(extractUriRoute(newRoute)));
    };

    (async () => {
      const routeGen = listenHashChangeEvent();
      await routeLoopIteration(routeGen);
      resolve();
      // noinspection InfiniteLoopJS
      while (true) {
        await routeLoopIteration(routeGen);
      }
    })();
  });

export const userOpensGcpApp =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    {
      changeLocation,
      changeLocationHash,
      listenHashChangeEvent,
      trackAction,
    }: IGcpAppIo,
  ) => {
    await startRouteLoop(dispatch, listenHashChangeEvent);

    const routerState = getRouterState(getState());

    if (!getViewMode(routerState)) {
      throw new Error("Page not found");
    }

    const redirectInfo = getRedirectForViewMode(routerState);

    if (redirectInfo) {
      changeLocationHash(dispatch(generateRedirectURL(redirectInfo)));
    }

    const [isUserLoggedIn, token] = await dispatch(ensureUserLoggedIn());

    trackAction(APP_IS_OPENED, {
      from: routerState.data.from,
      pageId: getViewMode(routerState),
    });

    if (isUserLoggedIn) {
      const payload = getPayloadFromToken(token);

      sentrySingleton.setUserContext(payload.user);

      if (config.intercom.enabled) {
        try {
          const [appId, userHash] = await dispatch(fetchUserIdentity());
          intercomSingleton.initIntercom(payload.user, appId, userHash);
        } catch (err) {
          sentrySingleton.captureException(err);
        }
      }
      return;
    }

    const referer = encodeURIComponent(
      restoreLocation(getRouterState(getState())),
    );
    const loginServiceBaseUrl = encodeURIComponent(
      config.endpoints.loginService,
    );

    changeLocation(
      `${config.endpoints.loginPage}/?referer=${referer}&loginServiceBaseUrl=${loginServiceBaseUrl}`,
    );
    throw new Error("User is not authorised, redirecting..");
  };

export const userClicksToBackofficeButton =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    await trackAction(TO_BACKOFFICE_SELECTED);
  };

export const userClicksToAsanaTaskButton =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    await trackAction(TO_ASANA_SELECTED);
  };

export const userClicksSidebarVisibilityControl =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    trackAction(SIDEBAR_OPENED);
    dispatch(toggleSidebarVisibility());
  };

export const userCollapsesActiveFmGroup =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    trackAction(ACTIVE_FM_COLLAPSED);
  };

export const userExpandsActiveFmGroup =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    trackAction(ACTIVE_FM_EXPANDED);
  };

export const userCollapsesRequestedFmGroup =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    trackAction(REQUESTED_FM_COLLAPSED);
  };

export const userExpandsRequestedFmGroup =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { trackAction }: IGcpAppIo,
  ) => {
    trackAction(REQUESTED_FM_EXPANDED);
  };
